import React, { useState, useEffect } from 'react';
import { FiArrowLeft, FiCheck } from 'react-icons/fi';
import '../../styles/Admin/Login.css';
import { updatePassword } from '../../lib/supabase/helpers';
import { supabase } from '../../lib/supabase/client';

function ResetPassword() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isValidToken, setIsValidToken] = useState(false);
  const [checkingToken, setCheckingToken] = useState(true);

  useEffect(() => {
    // Check if we have a valid session from the reset link
    checkSession();
  }, []);

  const checkSession = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Session check error:', error);
        setError('Invalid or expired reset link');
        setIsValidToken(false);
      } else if (session) {
        setIsValidToken(true);
      } else {
        setError('Invalid or expired reset link');
        setIsValidToken(false);
      }
    } catch (err) {
      console.error('Session check exception:', err);
      setError('An error occurred');
      setIsValidToken(false);
    } finally {
      setCheckingToken(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      const result = await updatePassword(newPassword);
      
      if (result.success) {
        setSuccess(true);
        setError('');
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          window.location.href = '/login';
        }, 3000);
      } else {
        setError(result.error || 'Failed to update password');
      }
    } catch (err) {
      console.error('Password update error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    window.location.href = '/login';
  };

  if (checkingToken) {
    return (
      <div className="login-page">
        <div className="login-container" style={{ justifyContent: 'center', alignItems: 'center' }}>
          <div className="loading-container" style={{ textAlign: 'center' }}>
            <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
            <p>Verifying reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isValidToken) {
    return (
      <div className="login-page">
        <button className="back-to-home" onClick={handleBackToLogin}>
          <FiArrowLeft /> Back to Login
        </button>

        <div className="login-container">
          <div className="login-right" style={{ margin: '0 auto' }}>
            <div className="login-form-wrapper">
              <div className="login-header">
                <h2>Invalid Reset Link</h2>
                <p>This password reset link is invalid or has expired</p>
              </div>

              <div className="alert-error">
                {error || 'Please request a new password reset link'}
              </div>

              <button
                type="button"
                onClick={handleBackToLogin}
                className="btn-login"
                style={{ marginTop: '20px' }}
              >
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <button className="back-to-home" onClick={handleBackToLogin}>
        <FiArrowLeft /> Back to Login
      </button>

      <div className="login-container">
        <div className="login-left">
          <div className="login-branding">
            <h1 className="brand-logo">Pip Pulse Fx</h1>
            <p className="brand-tagline">Admin Portal</p>
          </div>
          <div className="login-illustration">
            <div className="illustration-circle"></div>
            <div className="illustration-icon"></div>
          </div>
          <p className="login-welcome">
            Create a new password for your account
          </p>
        </div>

        <div className="login-right">
          <div className="login-form-wrapper">
            {success ? (
              <>
                <div className="login-header">
                  <h2>Password Reset Successful!</h2>
                  <p>Your password has been updated</p>
                </div>

                <div className="alert-success">
                  <FiCheck style={{ marginRight: '8px' }} />
                  Password updated successfully! Redirecting to login...
                </div>

                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="btn-login"
                  style={{ marginTop: '20px' }}
                >
                  Go to Login
                </button>
              </>
            ) : (
              <>
                <div className="login-header">
                  <h2>Set New Password</h2>
                  <p>Enter your new password below</p>
                </div>

                {error && (
                  <div className="alert-error">
                    {error}
                  </div>
                )}

                <form className="login-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="new-password">New Password</label>
                    <div className="input-wrapper">
                      <span className="input-icon"></span>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="new-password"
                        name="new-password"
                        placeholder="Enter new password (min 6 characters)"
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          setError('');
                        }}
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirm-password">Confirm New Password</label>
                    <div className="input-wrapper">
                      <span className="input-icon"></span>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirm-password"
                        name="confirm-password"
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          setError('');
                        }}
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                  </div>

                  <div style={{ 
                    fontSize: '13px', 
                    color: '#6b7280', 
                    marginBottom: '20px',
                    padding: '12px',
                    background: '#f9fafb',
                    borderRadius: '6px'
                  }}>
                    <strong>Password Requirements:</strong>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                      <li>At least 6 characters long</li>
                      <li>Must match in both fields</li>
                    </ul>
                  </div>

                  <button
                    type="submit"
                    className={`btn-login ${isLoading ? 'loading' : ''}`}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner"></span>
                        Updating Password...
                      </>
                    ) : (
                      'Update Password'
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="login-page-footer">
        <p>© {new Date().getFullYear()} Pip Pulse Fx. All rights reserved.</p>
      </div>
    </div>
  );
}

export default ResetPassword;